import { useState } from "react"
import { Button } from "@/components/ui/button"
import { AppointmentDialogFooter } from "../AppointmentDialogFooter"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { extractApiError } from "@/lib/api-errors"
import { CalThrottledNotice, staffCalThrottledMessage } from "@/components/CalThrottledNotice"
import { isCalThrottled, retryAfterSeconds } from "@/lib/calThrottled"
import { useGetReassignOptionsQuery } from "../../api/reassignApi"
import type { ReassignOption } from "../../api/reassignApi"
import { useReassignBookingAsStaffMutation } from "../../bookingCalendarApi"
import {
  HOST_ALREADY_BOOKED_CODE,
  doubleBookingFields,
  isHostAlreadyBooked,
  isOverrideReasonValid,
} from "../../lib/doubleBooking"
import { DAILY_LIMIT_REACHED_CODE, dailyLimitFields, isDailyLimitRefusal } from "../../lib/dailyLimit"
import { DailyLimitOverrideSection } from "../DailyLimitOverrideSection"
import { DoubleBookOverrideSection } from "../DoubleBookOverrideSection"
import type { BookingAction, BookingActionFormProps } from "./types"
import { MAX_NOTE_LENGTH, isChangeable } from "./types"
import { useReportBusy } from "./useReportBusy"

/** The server's reason, in words fit for the picker; null for somebody available. */
export function reasonLabel(option: ReassignOption): string | null {
  if (option.available) return null

  switch (option.reason) {
    case "booked":
      return "booked at this time"
    case "away": {
      const note = option.note?.trim()
      return note ? `away: ${note}` : "away"
    }
    case "daily-cap":
      return "daily limit reached"
    case "not-scheduled":
      return "not scheduled"
    default:
      return "unavailable"
  }
}

/** One row of the picker. */
export interface ReassignChoice {
  value: string
  calUserId: number
  name: string
  available: boolean
  /** Why not, from `reasonLabel`; absent for somebody available. */
  detail?: string
  /** Not choosable: unavailable, and the override that reaches them is off. */
  disabled: boolean
  booked: boolean
  /** At their daily limit that day (AB#606): reached by "Book past their daily limit". */
  capped: boolean
}

/** Which overrides can reach an unavailable row, besides the working-hours one. */
export interface ReassignReach {
  /** May double-book (`appointments:manage`): a booked row is choosable, and asks for the reason. */
  canDoubleBook?: boolean
  /** "Book past their daily limit" is ticked: a capped row is choosable (AB#606). */
  pastDailyLimit?: boolean
}

/**
 * The picker's rows: available staff first, then the rest, each in the server's order (by name).
 *
 * The unavailable are listed rather than hidden so a manager can see why, and are disabled until an
 * override that reaches them is on — the working-hours one reaches everybody (covering an absence);
 * somebody booked then is reachable by whoever may double-book, who is asked for the reason; somebody
 * at their daily limit by "Book past their daily limit" (AB#606).
 */
export function reassignChoices(
  options: readonly ReassignOption[],
  overriding: boolean,
  reach: ReassignReach = {},
): ReassignChoice[] {
  const available = options.filter(option => option.available)
  const unavailable = options.filter(option => !option.available)

  return [...available, ...unavailable].map(option => {
    const detail = reasonLabel(option) ?? undefined
    const booked = option.reason === "booked"
    const capped = option.reason === "daily-cap"
    const reachable = overriding
      || (booked && reach.canDoubleBook === true)
      || (capped && reach.pastDailyLimit === true)
    return {
      value: String(option.calUserId),
      calUserId: option.calUserId,
      name: option.name,
      available: option.available,
      ...(detail ? { detail } : {}),
      disabled: !option.available && !reachable,
      booked,
      capped,
    }
  })
}

/**
 * Moves the appointment to another host: a replacement booking, then the original cancelled. Offered on
 * every booking that can still be changed by somebody who manages appointments; who it could go to is
 * the server's answer, fetched when the form opens.
 */
export const ReassignAction: BookingAction = {
  id: "reassigning",
  label: "Move to another host",
  triggerVariant: "outline",
  isAvailable: ctx => isChangeable(ctx),
  Form: ReassignForm,
}

function ReassignForm({ ctx, onDone, onClose, onBusyChange, onResult }: BookingActionFormProps) {
  const [reassignBooking, reassignState] = useReassignBookingAsStaffMutation()
  const [note, setNote] = useState("")
  const [targetHostId, setTargetHostId] = useState<string>("")
  const [overrideConflicts, setOverrideConflicts] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Cal throttled the move (503 calThrottled, AB#514): the busy notice instead of `error`. */
  const [throttled, setThrottled] = useState<unknown>(null)

  /** The server said the chosen host is booked then (409 hostAlreadyBooked) — for a host the picker did not know about. */
  const [serverSaysBooked, setServerSaysBooked] = useState<string | null>(null)

  /** "Book past their daily limit" (AB#606): sends `overrideDailyLimit`, with the shared reason. */
  const [pastDailyLimit, setPastDailyLimit] = useState(false)

  /** The server said the chosen host is at their daily limit (409 dailyLimitReached). */
  const [serverSaysCapped, setServerSaysCapped] = useState<string | null>(null)

  /** The one reason both overrides share — what `overrideReason` carries. */
  const [doubleBookReason, setDoubleBookReason] = useState("")

  const busy = reassignState.isLoading
  useReportBusy(onBusyChange, busy)

  const { uid, attendeeName } = ctx

  // The server's list, asked for when the form opens — it leaves out the current host and the
  // service account, and says why each unavailable person is.
  const optionsQuery = useGetReassignOptionsQuery(uid, { skip: !uid })

  // Only `appointments:manage` may double-book or book past the daily limit, and every caller here
  // holds it (see isChangeable) — checked anyway, so the offer never outlives a change to that rule.
  const canDoubleBook = ctx.canManage
  const choices = reassignChoices(optionsQuery.data?.options ?? [], overrideConflicts, { canDoubleBook, pastDailyLimit })
  const availableChoices = choices.filter(choice => choice.available)
  const unavailableChoices = choices.filter(choice => !choice.available)

  // Said as soon as they are picked rather than after a round trip: the server refuses the move
  // anyway unless an administrator double-books, and the button should not be offering a plain move.
  const chosenHost = choices.find(choice => choice.value === targetHostId)
  const chosenHostBooked = chosenHost?.booked === true || serverSaysBooked !== null
  const doubleBooking = canDoubleBook && chosenHostBooked

  // What is sent is what the box says. Picking a host the picker knows is capped ticks it, and so
  // does the server's 409 for one it did not know about (AB#606).
  const bookingPastLimit = canDoubleBook && pastDailyLimit
  const needsReason = doubleBooking || bookingPastLimit

  function chooseHost(value: string) {
    setTargetHostId(value)
    setServerSaysBooked(null)
    setServerSaysCapped(null)
    setError(null)
    if (canDoubleBook && choices.find(choice => choice.value === value)?.capped) setPastDailyLimit(true)
  }

  // Turning an override off takes back a choice only it allowed.
  function setOverriding(on: boolean) {
    setOverrideConflicts(on)
    if (!on && chosenHost && !chosenHost.available && !(chosenHost.booked && canDoubleBook) && !(chosenHost.capped && pastDailyLimit)) {
      chooseHost("")
    }
  }

  function setPastLimit(on: boolean) {
    setPastDailyLimit(on)
    if (!on) {
      setServerSaysCapped(null)
      if (chosenHost?.capped && !overrideConflicts) chooseHost("")
    }
  }

  async function submitReassignment() {
    if (!uid || !targetHostId) return
    if (needsReason && !isOverrideReasonValid(doubleBookReason)) return

    setError(null)
    setThrottled(null)

    const result = await reassignBooking({
      bookingUid: uid,
      targetCalUserId: Number(targetHostId),
      reason: note.trim() || undefined,
      override: overrideConflicts || undefined,
      ...doubleBookingFields(doubleBooking, doubleBookReason),
      ...dailyLimitFields(bookingPastLimit, doubleBookReason),
    })

    if ("error" in result) {
      // The host turned out to be at their daily limit (AB#606): a manager is offered the override,
      // ticked for them, and still has to give the reason and press the button again.
      if (canDoubleBook && !bookingPastLimit && isDailyLimitRefusal(result.error)) {
        setServerSaysCapped(
          extractApiError((result.error as { data?: unknown }).data)
          || "That host has already reached their daily limit on this day.")
        setPastDailyLimit(true)
        return
      }

      // The host turned out to be booked, which the picker did not know: an administrator is offered
      // the override; anybody else is told.
      if (canDoubleBook && !doubleBooking && isHostAlreadyBooked(result.error)) {
        setServerSaysBooked(
          extractApiError((result.error as { data?: unknown }).data)
          || "That host already has an appointment at this time.")
        return
      }

      // Nothing moved: Cal turned the request away before it was made. Retried from the notice, by hand.
      if (isCalThrottled(result.error)) {
        setThrottled(result.error)
        onResult?.({ kind: "failed", detail: staffCalThrottledMessage(retryAfterSeconds(result.error)) })
        return
      }

      const message = describeReassignFailure(result.error)
      setError(message)
      onResult?.({ kind: "failed", detail: message })
      return
    }

    onResult?.({ kind: "reassigned" })

    // Success means the uid this dialog was opened on has been cancelled and replaced, so there is
    // nothing here that still addresses a live appointment.
    onClose()
  }

  return (
    <>
      <section>
        <Label htmlFor="reassign-host" className="text-xs font-medium">
          Move to
        </Label>
        <Select value={targetHostId} onValueChange={chooseHost} disabled={busy || choices.length === 0}>
          <SelectTrigger id="reassign-host" className="mt-1.5 w-full">
            <SelectValue placeholder={optionsQuery.isLoading ? "Loading staff…" : "Choose a host"} />
          </SelectTrigger>
          <SelectContent>
            {availableChoices.length > 0 && (
              <SelectGroup>
                <SelectLabel>Available</SelectLabel>
                {availableChoices.map(choice => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {unavailableChoices.length > 0 && (
              <SelectGroup>
                <SelectLabel>Unavailable</SelectLabel>
                {unavailableChoices.map(choice => (
                  <SelectItem key={choice.value} value={choice.value} disabled={choice.disabled}>
                    {choice.name}
                    <span className="text-muted-foreground">({choice.detail})</span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        {optionsQuery.isError && isCalThrottled(optionsQuery.error) ? (
          <CalThrottledNotice
            error={optionsQuery.error}
            onRetry={() => void optionsQuery.refetch()}
            busy={optionsQuery.isFetching}
            className="mt-1.5 text-xs"
          />
        ) : optionsQuery.isError && (
          <p role="alert" className="mt-1.5 text-xs text-red-700">
            {describeOptionsFailure(optionsQuery.error)}
          </p>
        )}
        {optionsQuery.isSuccess && choices.length === 0 && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            There is nobody else on this site's team to move it to.
          </p>
        )}
        {optionsQuery.isSuccess && choices.length > 0 && availableChoices.length === 0 && !overrideConflicts && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            Nobody is free at this time. To cover an absence, move it anyway below.
          </p>
        )}
        {chosenHostBooked && !canDoubleBook && (
          <p role="alert" className="mt-1.5 text-xs text-red-700">
            {chosenHost?.name ?? "That host"} already has an appointment at this time. Choose another
            host, or move their appointment first.
          </p>
        )}
        {doubleBooking && (
          <div className="mt-3">
            <DoubleBookOverrideSection
              id="reassign-double-book-reason"
              message={serverSaysBooked ?? `${chosenHost?.name ?? "That host"} already has an appointment at this time.`}
              reason={doubleBookReason}
              onReasonChange={setDoubleBookReason}
              disabled={busy}
            />
          </div>
        )}
        {/* One reason box between the two overrides: the server takes one reason for both, so with
            a double booking as well the box above already asks for it. */}
        {bookingPastLimit && !doubleBooking && (
          <div className="mt-3">
            <DailyLimitOverrideSection
              id="reassign-daily-limit-reason"
              message={serverSaysCapped ?? `${chosenHost?.name ?? "That host"} has already reached their daily limit on this day.`}
              reason={doubleBookReason}
              onReasonChange={setDoubleBookReason}
              disabled={busy}
            />
          </div>
        )}

        <Label htmlFor="reassign-reason" className="mt-3 block text-xs font-medium">
          Reason <span className="text-muted-foreground">(optional)</span>
        </Label>
        <Textarea
          id="reassign-reason"
          className="mt-1.5"
          rows={2}
          maxLength={MAX_NOTE_LENGTH}
          value={note}
          disabled={busy}
          placeholder="e.g. Covering for a colleague who is out"
          onChange={event => setNote(event.target.value)}
        />

        {/* Not a warning to be dismissed but the thing a supervisor is actually here to do:
            cover an absence by putting the appointment on somebody who is not rostered for it.
            Off by default all the same. It does not reach a host who is already booked — that is
            the double-booking override above, which needs a reason of its own — nor one at their
            daily limit, which is the box below (AB#606). */}
        <label className="mt-3 flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={overrideConflicts}
            disabled={busy}
            onChange={event => setOverriding(event.target.checked)}
          />
          <span>
            <span className="font-medium">Move it even if they are not scheduled to work</span>
            <span className="block text-muted-foreground">
              Lets you choose somebody listed as unavailable, and books them outside their hours, to
              cover an absence. It does not book them past their daily limit (that is the option
              below) or over an appointment they already have: that needs a double-booking reason.
            </span>
          </span>
        </label>

        {/* AB#606: the cap is the server's own count, and only this gets past it. Sends
            overrideDailyLimit with the reason the override panel asks for. */}
        {canDoubleBook && (
          <label className="mt-3 flex items-start gap-2 text-xs">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={pastDailyLimit}
              disabled={busy}
              onChange={event => setPastLimit(event.target.checked)}
            />
            <span>
              <span className="font-medium">Book past their daily limit</span>
              <span className="block text-muted-foreground">
                Lets you choose somebody who has already reached their daily limit of appointments
                that day. Needs a reason, recorded against your name on the appointment.
              </span>
            </span>
          </label>
        )}

        {/* The appointment is recreated rather than edited — Cal has no way to hand a booking to
            someone else — so the household is told twice and the reference changes. Staff are
            the ones who field the resulting phone call, so they are told first. */}
        <p className="mt-3 text-xs text-muted-foreground">
          {attendeeName ?? "The attendee"} will receive a cancellation and a new confirmation.
          The time stays the same and their confirmation number still works, but the booking
          reference changes.
        </p>
      </section>

      {throttled ? (
        <CalThrottledNotice error={throttled} onRetry={() => void submitReassignment()} />
      ) : error && <p role="alert" className="text-sm text-red-700">{error}</p>}

      <AppointmentDialogFooter>
        <Button
          variant="outline"
          disabled={busy}
          onClick={onDone}
        >
          Cancel
        </Button>
        {needsReason ? (
          <Button
            variant="destructive"
            disabled={busy || !targetHostId || !isOverrideReasonValid(doubleBookReason)}
            onClick={submitReassignment}
          >
            {reassignState.isLoading ? "Moving…" : doubleBooking ? "Double-book anyway" : "Move past daily limit"}
          </Button>
        ) : (
          <Button disabled={busy || !targetHostId || chosenHostBooked} onClick={submitReassignment}>
            {reassignState.isLoading ? "Moving…" : "Move appointment"}
          </Button>
        )}
      </AppointmentDialogFooter>
    </>
  )
}

/** Why the list of staff could not be shown. */
export function describeOptionsFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }

  if (failure?.status === 403) return "You do not have permission to move appointments."
  if (failure?.status === 404) return "This appointment is no longer in the scheduling system."
  return extractApiError(failure?.data) || "Who could take this appointment could not be loaded. Try again."
}

/**
 * Turns a failed reassignment into something worth reading.
 *
 * Leans on the server's own message, because the distinction that matters to the person reading it
 * — whether the appointment is still safely where it was — is one only the server knows. The two
 * outcomes that leave the calendar changed say so in their own text, so nothing here should paper
 * over it with a generic "could not be moved".
 */
function describeReassignFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)

  if (failure?.status === 403) return "You do not have permission to move appointments."
  if (failure?.status === 404) return "This appointment is no longer in the scheduling system."
  if (detail) return detail
  if ((failure?.data as { code?: unknown } | undefined)?.code === HOST_ALREADY_BOOKED_CODE) {
    return "That host already has an appointment at this time. This appointment has not been changed."
  }
  if ((failure?.data as { code?: unknown } | undefined)?.code === DAILY_LIMIT_REACHED_CODE) {
    return "That host has already reached their daily limit on this day. This appointment has not been changed."
  }

  return "The appointment could not be moved. It has been left with its original host."
}
